import { motion } from "framer-motion";
import { CloudLightning, TrendingDown, Landmark, Coins, Network } from "lucide-react";

const RISK_TYPES = [
    {
        icon: CloudLightning,
        label: "Physical Risk",
        summary: "Direct impact of climate hazards on assets, operations, and counterparty resilience.",
        signals: [
            "Flood frequency & depth",
            "Heat stress indices",
            "Water stress indicators",
            "Asset-level hazard exposure",
            "Sector vulnerability coefficients",
            "Expected Annual Loss (EAL)"
        ]
    },
    {
        icon: TrendingDown,
        label: "Transition Risk",
        summary: "Repricing driven by the shift to a lower-carbon economy — policy, tech, market substitution.",
        signals: [
            "Carbon pricing exposure",
            "Policy tightening trajectories",
            "Technology substitution risk",
            "Supply-chain decarbonisation mandates",
            "Stranded asset probability",
            "Margin compression"
        ]
    },
    {
        icon: Landmark,
        label: "Regulatory Risk",
        summary: "Exposure to evolving climate disclosure, compliance, and supervisory frameworks.",
        signals: [
            "EU CBAM relevance",
            "Disclosure completeness",
            "Methodological alignment",
            "Audit defensibility",
            "Regulatory stress alerts",
            "Litigation & legal exposure"
        ]
    },
    {
        icon: Coins,
        label: "Financial Risk",
        summary: "Balance-sheet and portfolio consequence of climate-linked exposure.",
        signals: [
            "Financed-emissions attribution (PCAF)",
            "Hidden concentration discovery",
            "Multi-borrower value-chain exposure",
            "Credit deterioration & migration",
            "Portfolio re-pricing needs",
            "Climate-VaR"
        ]
    },
    {
        icon: Network,
        label: "Operational Risk",
        summary: "Supply-chain and counterparty exposure that translates into business continuity risk.",
        signals: [
            "Supplier emissions concentration",
            "Counterparty dependency",
            "Supply-chain fragility index",
            "Disruption & contagion pathways",
            "Substitutability constraints",
            "Delisting exposure"
        ]
    }
];

export default function RiskTypes() {
    return (
        <section
            className="relative py-24 lg:py-32 border-t border-navy/10 bg-navy/[0.02]"
            data-testid="section-risk-types"
        >
            <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-16">
                    <div className="lg:col-span-6">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-10 h-px bg-gold"></div>
                            <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-gold">
                                Risk Taxonomy
                            </span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em] text-navy">
                            Five risk classes. <em className="text-gold not-italic font-light">One score.</em>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8 lg:self-end">
                        <p className="text-navy/60 text-lg leading-relaxed">
                            Carbonis unifies physical, transition, regulatory, financial, and operational climate risk into a single confidence-weighted score — the Carbonis Risk Exposure Index (CRX™).
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-navy/10 border border-navy/10">
                    {RISK_TYPES.map((r, i) => {
                        const Icon = r.icon;
                        return (
                            <motion.div
                                key={r.label}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.7, delay: i * 0.08 }}
                                className="bg-paper p-7 lg:p-8 flex flex-col gap-5 min-h-[440px]"
                                data-testid={`risk-type-${r.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                                <Icon size={26} strokeWidth={1.25} className="text-gold" />
                                <div>
                                    <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-navy/50 mb-3">
                                        {String(i + 1).padStart(2, "0")} · Risk Class
                                    </div>
                                    <h3 className="font-serif text-2xl leading-[1.1] tracking-[-0.01em] text-navy">
                                        {r.label}
                                    </h3>
                                </div>
                                <p className="text-navy/60 text-[13.5px] leading-relaxed">
                                    {r.summary}
                                </p>
                                <div className="mt-auto pt-5 border-t border-navy/10 flex flex-col gap-1.5">
                                    {r.signals.map((s) => (
                                        <div
                                            key={s}
                                            className="font-mono text-[10px] tracking-[0.12em] uppercase text-navy/55 flex items-start gap-1.5"
                                        >
                                            <span className="text-gold mt-[3px]">·</span>
                                            <span>{s}</span>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
